import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import type { LensDivProps, ResponsiveType } from '../../types';
import {
  getRadius,
  getSize,
  getSizeValue,
  getColorValue,
} from '../../utilities';

const ssrComment = `/* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */`;

const variantStyles = {
  border: css`
    .ListRowWrapper:last-child {
      border-bottom: 1px solid ${getColorValue('border')};
    }

    .ListRowWrapper,
    .ListRowWrapper:first-child ${ssrComment} {
      border-top: 1px solid ${getColorValue('border')};
    }
  `,
  stripe: css`
    .ListRowWrapper {
      &:nth-child(odd) ${ssrComment} {
        background-color: ${getColorValue('backgroundSecondary')};
      }
    }

    .ListRowWrapper {
      ${getRadius('medium')};
    }
  `,
  clear: css``,
};

const ListWrapper = styled.div<ListProps>`
  .ListRowWrapper {
    grid-template-columns: ${props => props.columns && props.columns};
    ${props => getSize('gap', props.gap)};
  }

  ${props => variantStyles[props.variant]};
`;

const ListRowWrapper = styled.div<ListRowWrapperProps>`
  display: grid;
  align-items: center;
  text-decoration: none;
  color: inherit;

  ${props => getSize('height', props.height)};
  ${props => getSize('min-height', props.minHeight)};
  ${props => getSize('max-height', props.maxHeight)};
  ${props => getSize('padding', props.padding)};
  ${props => getSize('padding-top', props.paddingTop)};
  ${props => getSize('padding-bottom', props.paddingBottom)};
  ${props => getSize('padding-left', props.paddingLeft)};
  ${props => getSize('padding-right', props.paddingRight)};

  ${props =>
    props.paddingY &&
    `
    ${getSize('padding-top', props.paddingY)}
    ${getSize('padding-bottom', props.paddingY)}
    `};

  ${props =>
    props.paddingX &&
    `
    ${getSize('padding-left', props.paddingX)}
    ${getSize('padding-right', props.paddingX)}
    `};

  ${props => (props.onClick || props.href) && `cursor: pointer;`};

  &.ListRowWrapper:nth-child(even),
  &.ListRowWrapper:nth-child(odd) ${ssrComment} {
    ${props =>
      props.backgroundColor &&
      `background-color: ${getColorValue(props.backgroundColor)}`};

    &:hover {
      ${props =>
        (props.onClick || props.href) &&
        `
      background-color: ${getColorValue('backgroundHover')};
      border-color: transparent;
      ${getRadius('medium')};
    `};
    }
  }
`;

export const ListRow = ({
  children,
  htmlTag = 'li',
  className,
  backgroundColor,
  onClick,
  href,
  role,
  tabIndex,
  ...props
}: ListRowProps) => {
  const classNameFromProp = className ? ` ${className}` : ``;

  const nonInteractiveElements = [
    'div',
    'span',
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'section',
    'article',
    'header',
    'footer',
    'main',
    'aside',
    'nav',
  ];

  const isAutoInteractive = !htmlTag && onClick;

  let isNonInteractive = false;
  const actualHtmlTag = htmlTag || 'div';
  if (!isAutoInteractive) {
    isNonInteractive = nonInteractiveElements.includes(actualHtmlTag);
  }

  const interactiveProps =
    (!isNonInteractive || isAutoInteractive) && (onClick || href)
      ? {
          onClick,
          onKeyDown: e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onClick?.(e);
            }
          },
          // Anchors are natively focusable; only non-anchor interactive elements need tabIndex.
          // Consumer-provided tabIndex takes precedence; fall back to 0 for interactive elements.
          ...(onClick && !href ? { tabIndex: tabIndex ?? 0 } : {}),
        }
      : {};

  return (
    <ListRowWrapper
      role={role || (htmlTag === 'li' ? 'listitem' : undefined)}
      className={`ListRowWrapper${classNameFromProp}`}
      as={htmlTag}
      backgroundColor={backgroundColor}
      href={href}
      {...interactiveProps}
      {...props}
    >
      {children}
    </ListRowWrapper>
  );
};

const formatColumns = columns =>
  columns.map(columnValue => getSizeValue(columnValue)).join(' ');

const List = ({
  children,
  columns,
  gap,
  variant = 'stripe',
  htmlTag = 'ul',
  ...props
}: ListProps &
  React.ComponentProps<typeof ListWrapper> &
  Omit<
    LensDivProps,
    keyof ListProps & React.ComponentProps<typeof ListWrapper>
  >) => {
  let processedChildren = children;
  if (htmlTag === 'ul') {
    processedChildren = React.Children.map(children, child => {
      if (React.isValidElement(child) && child.type === ListRow) {
        return React.cloneElement(child as React.ReactElement<ListRowProps>, {
          htmlTag: 'li',
        });
      }
      return child;
    });
  }

  return (
    <ListWrapper
      as={htmlTag}
      columns={columns && formatColumns(columns)}
      gap={gap}
      variant={variant}
      role={htmlTag === 'ul' ? 'list' : undefined}
      {...props}
    >
      {processedChildren}
    </ListWrapper>
  );
};

interface SharedProps {
  padding?: ResponsiveType<string | number>;
  paddingTop?: ResponsiveType<string | number>;
  paddingBottom?: ResponsiveType<string | number>;
  paddingLeft?: ResponsiveType<string | number>;
  paddingRight?: ResponsiveType<string | number>;
  paddingY?: ResponsiveType<string | number>;
  paddingX?: ResponsiveType<string | number>;
  height?: ResponsiveType<string | number>;
  minHeight?: ResponsiveType<string | number>;
  maxHeight?: ResponsiveType<string | number>;
  backgroundColor?: string;
}

interface ListRowProps extends SharedProps {
  className?: string;
  htmlTag?: keyof JSX.IntrinsicElements;
  children?: React.ReactNode;
  onClick?: React.ReactEventHandler;
  href?: string;
  role?: string;
  tabIndex?: number;
}

interface ListRowWrapperProps extends SharedProps {
  className?: string;
  as?: string;
  children?: React.ReactNode;
  href?: string;
  onClick?: React.ReactEventHandler;
}

type ListProps = {
  columns?: (string | number)[];
  gap?: ResponsiveType<string | number>;
  variant?: 'clear' | 'border' | 'stripe';
  htmlTag?: keyof JSX.IntrinsicElements;
  backgroundColor?: string;
  children?: React.ReactNode;
};

export default List;
